import { defineComponent } from "../lib/component.ts";

interface CollageGridRefs {
  items?: HTMLElement[];
}

const isFocusable = (el: HTMLElement): boolean => el.hasAttribute("tabindex");

class CollageGrid extends defineComponent<CollageGridRefs>()([]) {
  connectedCallback(): void {
    super.connectedCallback();

    const focusable = (this.refs.items ?? []).filter(isFocusable);
    if (focusable.length === 0) return;

    for (const [index, item] of focusable.entries()) {
      item.tabIndex = index === 0 ? 0 : -1;
    }

    this.addEventListener("keydown", this.#handleKeydown, { signal: this.signal });
    this.addEventListener("focusin", this.#handleFocusIn, { signal: this.signal });
  }

  #handleFocusIn = (event: FocusEvent): void => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const focusable = (this.refs.items ?? []).filter(isFocusable);
    if (!focusable.includes(target)) return;
    for (const item of focusable) {
      item.tabIndex = item === target ? 0 : -1;
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    const items = this.refs.items ?? [];
    if (items.length === 0) return;

    const active = document.activeElement;
    const currentIndex = items.findIndex((item) => item === active);
    if (currentIndex === -1) return;

    const columns = this.#columnCount();
    let target: number;
    let searchDir: 1 | -1;

    switch (event.key) {
      case "ArrowRight":
        target = currentIndex + 1;
        searchDir = 1;
        break;
      case "ArrowLeft":
        target = currentIndex - 1;
        searchDir = -1;
        break;
      case "ArrowDown":
        target = currentIndex + columns;
        searchDir = 1;
        break;
      case "ArrowUp":
        target = currentIndex - columns;
        searchDir = -1;
        break;
      case "Home":
        target = 0;
        searchDir = 1;
        break;
      case "End":
        target = items.length - 1;
        searchDir = -1;
        break;
      default:
        return;
    }

    event.preventDefault();
    target = Math.max(0, Math.min(items.length - 1, target));

    while (target >= 0 && target < items.length && !isFocusable(items[target]!)) {
      target += searchDir;
    }

    if (target < 0 || target >= items.length || target === currentIndex) return;
    items[target]?.focus();
  };

  #columnCount(): number {
    const template = window.getComputedStyle(this).gridTemplateColumns;
    if (!template || template === "none") return 1;
    // Split on whitespace outside parentheses so `minmax(0px, 1fr)` counts as one track.
    const tracks = template.split(/\s+(?![^(]*\))/).filter(Boolean);
    return tracks.length || 1;
  }
}

if (!customElements.get("collage-grid")) {
  customElements.define("collage-grid", CollageGrid);
}
